import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categoryBanners: Record<string, string> = {
  dianying: 'https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20with%20warm%20spotlight%20and%20theater%20curtains%2C%20golden%20amber%20lighting%2C%20artistic%20minimal%20style%2C%20elegant%20atmosphere&width=1800&height=500&seq=cat-banner-movie&orientation=landscape',
  dianshiju: 'https://readdy.ai/api/search-image?query=Cozy%20living%20room%20with%20television%20screen%20glowing%2C%20warm%20evening%20ambiance%2C%20soft%20lamp%20light%2C%20artistic%20minimal%20style&width=1800&height=500&seq=cat-banner-tv&orientation=landscape',
  zongyi: 'https://readdy.ai/api/search-image?query=Colorful%20stage%20with%20soft%20spotlights%20and%20music%20atmosphere%2C%20vibrant%20warm%20colors%2C%20artistic%20abstract%20style&width=1800&height=500&seq=cat-banner-variety&orientation=landscape',
  jilupian: 'https://readdy.ai/api/search-image?query=Majestic%20mountain%20landscape%20through%20camera%20lens%2C%20warm%20earthy%20tones%2C%20golden%20hour%20lighting%2C%20artistic%20minimal%20style&width=1800&height=500&seq=cat-banner-doc&orientation=landscape',
  dongman: 'https://readdy.ai/api/search-image?query=Vibrant%20anime%20art%20style%20illustration%20with%20warm%20gradient%20colors%2C%20creative%20composition%2C%20soft%20lighting%2C%20artistic%20minimal&width=1800&height=500&seq=cat-banner-anime&orientation=landscape',
  shaoer: 'https://readdy.ai/api/search-image?query=Colorful%20playful%20children%20toys%20and%20books%20arrangement%2C%20warm%20pastel%20colors%2C%20soft%20natural%20light%2C%20artistic%20minimal%20style&width=1800&height=500&seq=cat-banner-kids&orientation=landscape',
};

const articleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Summer%20movie%20theater%20with%20warm%20golden%20lighting%2C%20cozy%20cinema%20vibe%2C%20artistic%20minimal%20style%2C%20soft%20bokeh&width=600&height=360&seq=cat-art-movie-01&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Smartphone%20on%20minimal%20desk%20with%20headphones%2C%20warm%20ambient%20lighting%2C%20modern%20clean%20aesthetic&width=600&height=360&seq=cat-art-phone-01&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Documentary%20camera%20lens%20with%20globe%2C%20warm%20earthy%20tones%2C%20artistic%20minimal%20style%2C%20soft%20natural%20light&width=600&height=360&seq=cat-art-doc-01&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=TV%20drama%20collage%20with%20warm%20cinematic%20lighting%2C%20modern%20living%20room%2C%20artistic%20minimal%20style&width=600&height=360&seq=cat-art-tv-01&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Variety%20show%20stage%20with%20colorful%20lights%2C%20vibrant%20warm%20atmosphere%2C%20artistic%20composition&width=600&height=360&seq=cat-art-variety-01&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Chinese%20animation%20art%20style%20collage%2C%20vibrant%20warm%20colors%2C%20creative%20composition%2C%20artistic%20minimal&width=600&height=360&seq=cat-art-anime-01&orientation=landscape',
};

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const bannerImage = categoryBanners[currentCategory.slug] || categoryBanners.dianying;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hfhwpsm.cn' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://hfhwpsm.cn/category/${currentCategory.slug}` },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${currentCategory.name} - 在线观看${currentCategory.name}高清视频 | 极速高清`}
        description={`极速高清${currentCategory.name}频道，${currentCategory.description}。共${currentCategory.itemCount}部${currentCategory.name}内容在线观看。`}
        keywords={`在线观看${currentCategory.name},${currentCategory.name}在线观看,免费${currentCategory.name},高清${currentCategory.name}`}
        canonicalUrl={`https://hfhwpsm.cn/category/${currentCategory.slug}`}
        ogType="website"
        schemaData={[breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="relative w-full h-[300px] md:h-[360px] overflow-hidden">
          <img
            src={bannerImage}
            alt={currentCategory.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-24 relative z-10">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-950 font-medium">{currentCategory.name}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-3">
              {currentCategory.name}
              <span className="text-primary-500 ml-2">在线观看</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-10 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/40 transition-all duration-300 hover:-translate-y-1 no-underline"
                >
                  <div className="relative aspect-[5/3] overflow-hidden">
                    <img
                      src={articleImages[article.id] || articleImages['article-1']}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-primary-500 text-background-50 rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-medium text-foreground-950 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views.toLocaleString()}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-foreground-500 text-base">该分类暂无内容，敬请期待</p>
              <Link
                to="/"
                className="inline-block mt-4 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default CategoryPage;